<script setup lang="ts">
    import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import chatService, { type ChatConversation, type ChatMessage } from '@/services/chatService';
    import { formatDate } from '../../utils';
    import { useNotificationStore } from '@/stores/notificationStore';
    import { onEvent, offEvent, joinRoom, leaveRoom, getSocket, isSocketConnected } from '@/config/socket';
    
    const route = useRoute();
    const router = useRouter();
    const notificationStore = useNotificationStore();
    const conversationId = ref<number | null>(null);
    const conversation = ref<ChatConversation | null>(null);
    const messages = ref<ChatMessage[]>([]);
    const newMessage = ref('');
    const isSending = ref(false);
    const loading = ref(false);
    const errorMessage = ref('');
    const messagesContainer = ref<HTMLElement | null>(null);
    let fallbackInterval: number | null = null;
    let connectionCheckInterval: number | null = null;
    let socketConnected = ref(true); // On assume que Socket.IO est connecté par défaut
    const recentlySentMessageIds = new Set<number>(); // Garder une trace des messages récemment envoyés

    // Callback pour les nouveaux messages reçus via Socket.IO
    const handleNewMessage = (data: any) => {
        console.log('[ChatDetail] Nouveau message reçu via Socket:', data);
        if (data.conversation_id === conversationId.value || data.message?.conversationId === conversationId.value) {
            // Ajouter le message s'il n'existe pas déjà
            const messageId = data.message?.id || data.id;
            
            // Vérifier si le message existe déjà dans la liste
            const messageExists = messages.value.some(m => m.id === messageId);
            
            // Vérifier si c'est un message qu'on vient d'envoyer (pour éviter les doublons)
            const isRecentlySent = recentlySentMessageIds.has(messageId);
            
            if (!messageExists && !isRecentlySent) {
                const newMessage: ChatMessage = data.message || {
                    id: messageId,
                    conversationId: conversationId.value!,
                    texte: data.texte || data.message?.texte || '',
                    date: data.date || data.message?.date || new Date().toISOString(),
                    statut: data.statut || data.message?.statut || 'sent',
                    qui: data.qui || data.message?.qui || (data.user_id ? 'user' : 'bot'),
                    metadata: data.metadata || data.message?.metadata || null,
                    agent: data.agent || data.message?.agent || null,
                };
                messages.value.push(newMessage);
                nextTick(() => scrollToBottom());
                
                // Si c'est un message utilisateur, le marquer comme lu immédiatement
                if (newMessage.qui === 'user') {
                    markMessagesAsRead();
                }
            } else if (isRecentlySent) {
                // Si c'est un message qu'on vient d'envoyer, on l'ignore car il est déjà dans la liste
                console.log('[ChatDetail] Message ignoré (déjà envoyé):', messageId);
                // Retirer de la liste des messages récents après un court délai
                setTimeout(() => {
                    recentlySentMessageIds.delete(messageId);
                }, 2000);
            }
        }
    };

    // Vérifier la connexion Socket.IO et activer le fallback si nécessaire
    function checkSocketConnection() {
        socketConnected.value = isSocketConnected();
        
        // Si Socket.IO n'est pas connecté, activer le polling de fallback
        if (!socketConnected.value && !fallbackInterval) {
            console.warn('[ChatDetail] Socket.IO non connecté, activation du polling de fallback');
            fallbackInterval = window.setInterval(async () => {
                if (conversationId.value) {
                    await loadMessages();
                }
            }, 10000); // Polling toutes les 10 secondes en fallback
        } else if (socketConnected.value && fallbackInterval) {
            // Si Socket.IO se reconnecte, désactiver le polling
            console.log('[ChatDetail] Socket.IO reconnecté, désactivation du polling');
            if (fallbackInterval) {
                clearInterval(fallbackInterval);
                fallbackInterval = null;
            }
        }
    }

    // Marquer les messages comme lus
    async function markMessagesAsRead() {
        if (conversationId.value) {
            await notificationStore.markConversationRead(conversationId.value);
        }
    }

    onMounted(async () => {
        const id = parseInt(route.params.id as string);
        if (isNaN(id)) {
            router.push('/app/service-client');
            return;
        }
        conversationId.value = id;
        await loadConversation();
        await loadMessages();
        
        // Marquer les messages comme lus à l'ouverture
        await markMessagesAsRead();
        
        // Vérifier la connexion Socket.IO
        checkSocketConnection();
        
        // Rejoindre le room de la conversation pour les mises à jour en temps réel
        joinRoom(`conversation.${id}`);
        
        // Écouter les nouveaux messages via Socket.IO
        onEvent('new.message', handleNewMessage);
        
        // Vérifier périodiquement la connexion Socket.IO (toutes les 30 secondes)
        connectionCheckInterval = window.setInterval(() => {
            checkSocketConnection();
        }, 30000);
    });

    onUnmounted(() => {
        // Désactiver le polling de fallback s'il est actif
        if (fallbackInterval) {
            clearInterval(fallbackInterval);
            fallbackInterval = null;
        }
        // Désactiver la vérification de connexion
        if (connectionCheckInterval) {
            clearInterval(connectionCheckInterval);
            connectionCheckInterval = null;
        }
        // Quitter le room et nettoyer les listeners
        if (conversationId.value) {
            leaveRoom(`conversation.${conversationId.value}`);
        }
        offEvent('new.message', handleNewMessage);
    });

    async function loadConversation() {
        try {
            loading.value = true;
            errorMessage.value = '';
            if (!conversationId.value) return;
            conversation.value = await chatService.getConversation(conversationId.value);
        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loading.value = false;
        }
    }

    async function loadMessages() {
        try {
            if (!conversationId.value) return;
            const currentMessagesCount = messages.value.length;
            const newMessages = await chatService.getMessages(conversationId.value);
            
            // Ne mettre à jour que si de nouveaux messages sont arrivés
            if (newMessages.length !== currentMessagesCount) {
                messages.value = newMessages;
                await nextTick();
                scrollToBottom();
            }
        } catch (error: any) {
            errorMessage.value = error.message;
        }
    }


    async function sendMessage() {
        if (!newMessage.value.trim() || !conversationId.value || isSending.value) return;

        try {
            isSending.value = true;
            errorMessage.value = '';
            
            const message = await chatService.sendMessage(conversationId.value, newMessage.value.trim());
            
            // Vérifier que le message n'existe pas déjà (éviter les doublons)
            const messageExists = messages.value.some(m => m.id === message.id);
            if (!messageExists) {
                messages.value.push(message);
            }
            
            // Ajouter l'ID du message à la liste des messages récemment envoyés
            // pour éviter qu'il soit ajouté à nouveau via Socket.IO
            if (message.id) {
                recentlySentMessageIds.add(message.id);
                // Retirer après 5 secondes (le temps que Socket.IO le renvoie)
                setTimeout(() => {
                    recentlySentMessageIds.delete(message.id);
                }, 5000);
            }
            
            newMessage.value = '';
            
            await nextTick();
            scrollToBottom();
        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            isSending.value = false;
        }
    }

    async function updateStatus(newStatus: 'open' | 'closed' | 'pending') {
        if (!conversationId.value) return;
        
        try {
            loading.value = true;
            await chatService.updateStatus(conversationId.value, newStatus);
            if (conversation.value) {
                conversation.value.statut = newStatus;
            }
        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loading.value = false;
        }
    }

    function scrollToBottom() {
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    }

    watch(messages, () => {
        nextTick(() => scrollToBottom());
    }, { deep: true });
</script>

<template>
    <main class="bg-chatbot">
        <div class="page-space-50"></div>
        <section class="container">
            <div class="h-20"></div>
            
            <!-- Header avec bouton retour et statut -->
            <div class="mb-5">
                <button class="btn white2 normal mb-4" @click="router.push('/app/service-client')">
                    <img src="/icons/icon-arrow-left.svg" class="btn-icon" alt="" />
                    <span>Retour</span>
                </button>
                <div class="flex align-items-center justify-content-between" v-if="conversation">
                    <div class="flex align-items-center">
                        <img 
                            v-if="conversation.user?.avatar" 
                            :src="conversation.user.avatar" 
                            class="size-2xl rounded-100 mr-4" 
                            alt=""
                        />
                        <div v-else class="size-2xl rounded-100 mr-4 bg-grey-200 flex align-items-center justify-content-center">
                            <span class="text-lg">{{ conversation.user?.prenom?.[0] || 'U' }}</span>
                        </div>
                        <div>
                            <h2 class="font-semibold mb-0">{{ conversation.user?.prenom }} {{ conversation.user?.nom }}</h2>
                            <h4 class="text-grey-500 mb-0">{{ conversation.user?.email }}</h4>
                        </div>
                    </div>
                    <div class="form-field-floating sm">
                        <select 
                            class="form-input-select" 
                            :value="conversation.statut"
                            @change="updateStatus(($event.target as HTMLSelectElement).value as 'open' | 'closed' | 'pending')"
                        >
                            <option value="open">Ouvert</option>
                            <option value="closed">Fermé</option>
                            <option value="pending">En attente</option>
                        </select>
                        <label>Statut</label>
                    </div>
                </div>
            </div>

            <!--Tchat-->
            <div class="widget-chatbot">
                <div class="widget-chatbot-header" v-if="conversation && conversation.agent">
                    <img 
                        :src="conversation.agent.avatar || '/icons/icon-user.svg'" 
                        class="size-2xl rounded-100 mr-4" 
                        alt="Agent" 
                    />
                    <div>
                        <h2 class="font-semibold">Service Client</h2>
                        <h4>{{ conversation.agent.prenom || 'Service client' }}</h4>
                    </div>
                </div>
                <div class="widget-chatbot-messages" ref="messagesContainer">
                    <!-- Loading -->
                    <template v-if="loading && messages.length === 0">
                        <p class="text-center my-5">Chargement des messages...</p>
                    </template>

                    <!-- Messages -->
                    <template v-else-if="messages.length === 0">
                        <p class="text-center my-5 text-grey-500">Aucun message dans cette conversation</p>
                    </template>
                    <template v-else>
                        <div 
                            v-for="(message, index) in messages" 
                            :key="message.id || index"
                            :class="['message', message.qui]"
                        >
                            {{ message.texte }}
                        </div>
                    </template>
                </div>
                <div class="widget-chatbot-send-message">
                    <div class="widget-chatbot-input-set">
                        <input 
                            v-model="newMessage"
                            type="text" 
                            class="widget-chatbot-input" 
                            placeholder="Écrire un message..." 
                            @keyup.enter="sendMessage"
                            :disabled="isSending"
                        />
                    </div>
                    <button 
                        class="btn-ultra tips send"
                        :class="{ 'loading': isSending }"
                        @click="sendMessage"
                        :disabled="!newMessage.trim() || isSending"
                    >
                        <div class="btn-show">
                            <img src="/icons/icon-send.svg" class="btn-icon" alt="" />
                        </div>
                        <div class="btn-loading">
                            <div class="loader-dots"></div>
                        </div>
                    </button>
                </div>
            </div>
            
            <div v-if="errorMessage" class="text-red text-sm mt-4 text-center">{{ errorMessage }}</div>
        </section>
    </main>
</template>
